/**
 * Fetch a protected file and expose it as an object URL.
 *
 * Attachments sit behind the bearer token (CLAUDE.md 15.3), so an `<img src>` or an
 * `<iframe src>` pointing at the API would arrive with no credentials. The bytes are
 * fetched with the token and handed to the element as a `blob:` URL instead, which
 * the CSP allows for images and frames (deploy/nginx-app.conf). The URL is revoked
 * when the component unmounts or the path changes.
 */
import { useEffect, useState } from 'react'

import { fetchFile } from '@/lib/download'

export type BlobState =
  { status: 'loading' } | { status: 'ready'; url: string } | { status: 'error' }

const LOADING: BlobState = { status: 'loading' }
const ERROR: BlobState = { status: 'error' }

export function useBlobUrl(path: string | null): BlobState {
  // The result remembers which path it belongs to, so a path change reads as
  // "loading" on the very next render without an effect resetting state.
  const [result, setResult] = useState<{ path: string; state: BlobState } | null>(null)

  useEffect(() => {
    if (!path) return
    const controller = new AbortController()
    let objectUrl: string | null = null

    void fetchFile(path, { signal: controller.signal }).then((blob) => {
      if (controller.signal.aborted) return
      if (!blob) {
        setResult({ path, state: ERROR })
        return
      }
      objectUrl = URL.createObjectURL(blob)
      setResult({ path, state: { status: 'ready', url: objectUrl } })
    })

    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [path])

  if (!path) return ERROR
  return result && result.path === path ? result.state : LOADING
}
